// International region (choropleth) helpers for the Tour Planner map.
//
// Companion to affiliateRegionMap.js (which is US-specific: state → metro →
// county → ZIP). This module provides a country-agnostic three-level division
// for every other country we have venues in:
//
//   ADM0 (country) → ADM1 (region / state / province) → ADM2 (department /
//   county / municipality ≈ "city / local").
//
// Boundary geometry is loaded on demand per country from geoBoundaries
// (gbOpen, CC-BY / ODbL), using the pre-simplified GeoJSON so payloads stay
// small. Geometry is fetched lazily — only for countries that actually have
// venues in the current viewport — and cached for the session.
//
// The US is intentionally excluded here: it keeps its richer state/county/ZIP
// tiers from affiliateRegionMap.js.

import { feature as topojsonFeature } from 'topojson-client';
import * as turf from '@turf/turf';

// geoBoundaries metadata endpoint. Returns JSON pointing at the (LFS-backed)
// GeoJSON download URLs, including a pre-simplified variant we prefer.
const GEOBOUNDARIES_API = 'https://www.geoboundaries.org/api/current/gbOpen';

// World country outlines (ADM0) for every country in one small bundle — the
// zoomed-out tier. Loaded once and cached so the country view is instant and
// makes ZERO per-country API calls; geoBoundaries is only hit for the deeper
// ADM1/ADM2 tiers when the user zooms in.
const WORLD_COUNTRIES_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
// world-atlas numeric ids to drop from ADM0: the US keeps its own richer
// state/county/ZIP tiers (us-atlas), so exclude it to avoid double-rendering.
const ADM0_EXCLUDE_IDS = new Set(['840']);

// Admin levels, shallow → deep. Index maps directly to a map-zoom band.
export const INTL_LEVELS = ['ADM0', 'ADM1', 'ADM2'];

// Venues store `country` as a full display name (see Admin.normalizeCountry),
// not an ISO code, so map names → ISO3 (what geoBoundaries keys on). Keys are
// lowercased; common alternate spellings included. The US is omitted on
// purpose (handled by affiliateRegionMap.js).
const COUNTRY_NAME_TO_ISO3 = {
  // ── Europe ────────────────────────────────────────────────────────────────
  'united kingdom': 'GBR', uk: 'GBR', 'u.k.': 'GBR', 'great britain': 'GBR',
  britain: 'GBR', england: 'GBR', scotland: 'GBR', wales: 'GBR',
  'northern ireland': 'GBR',
  ireland: 'IRL', 'republic of ireland': 'IRL', eire: 'IRL', ireleand: 'IRL',
  france: 'FRA',
  germany: 'DEU', deutschland: 'DEU', bundesrepublik: 'DEU',
  spain: 'ESP', 'españa': 'ESP', espana: 'ESP',
  portugal: 'PRT',
  italy: 'ITA', italia: 'ITA',
  netherlands: 'NLD', 'the netherlands': 'NLD', holland: 'NLD', nederland: 'NLD',
  belgium: 'BEL', 'belgië': 'BEL', belgie: 'BEL', belgique: 'BEL',
  luxembourg: 'LUX',
  switzerland: 'CHE', schweiz: 'CHE', suisse: 'CHE', svizzera: 'CHE',
  austria: 'AUT', 'österreich': 'AUT', osterreich: 'AUT',
  denmark: 'DNK', danmark: 'DNK',
  sweden: 'SWE', sverige: 'SWE',
  norway: 'NOR', norge: 'NOR',
  finland: 'FIN', suomi: 'FIN',
  iceland: 'ISL',
  poland: 'POL', polska: 'POL',
  'czech republic': 'CZE', czechia: 'CZE', 'česká republika': 'CZE',
  'ceska republika': 'CZE',
  slovakia: 'SVK', slovak: 'SVK',
  hungary: 'HUN', 'magyarország': 'HUN', magyarorszag: 'HUN',
  romania: 'ROU',
  bulgaria: 'BGR',
  greece: 'GRC', hellas: 'GRC',
  croatia: 'HRV', hrvatska: 'HRV',
  slovenia: 'SVN', slovenija: 'SVN',
  serbia: 'SRB',
  'bosnia and herzegovina': 'BIH', bosnia: 'BIH',
  'north macedonia': 'MKD', macedonia: 'MKD',
  montenegro: 'MNE',
  albania: 'ALB',
  kosovo: 'XKX',
  estonia: 'EST', latvia: 'LVA', lithuania: 'LTU',
  ukraine: 'UKR',
  belarus: 'BLR',
  moldova: 'MDA',
  russia: 'RUS', 'russian federation': 'RUS',
  turkey: 'TUR', turkiye: 'TUR', 'türkiye': 'TUR',
  malta: 'MLT', cyprus: 'CYP',

  // ── South America ───────────────────────────────────────────────────────────
  argentina: 'ARG',
  bolivia: 'BOL',
  brazil: 'BRA', brasil: 'BRA',
  chile: 'CHL',
  colombia: 'COL',
  ecuador: 'ECU',
  guyana: 'GUY',
  paraguay: 'PRY',
  peru: 'PER', 'perú': 'PER',
  suriname: 'SUR',
  uruguay: 'URY',
  venezuela: 'VEN',

  // ── Asia ────────────────────────────────────────────────────────────────────
  japan: 'JPN', nippon: 'JPN', nihon: 'JPN', '日本': 'JPN',
  'south korea': 'KOR', korea: 'KOR', 'republic of korea': 'KOR',
  hanguk: 'KOR', '한국': 'KOR',
  china: 'CHN', 'zhongguo': 'CHN', '中国': 'CHN',
  'hong kong': 'HKG',
  taiwan: 'TWN',
  india: 'IND',
  indonesia: 'IDN',
  thailand: 'THA',
  vietnam: 'VNM', 'viet nam': 'VNM',
  philippines: 'PHL',
  malaysia: 'MYS',
  singapore: 'SGP',
  israel: 'ISR',
  'united arab emirates': 'ARE', uae: 'ARE',

  // ── North & Central America (non-US) ─────────────────────────────────────────
  canada: 'CAN',
  mexico: 'MEX', 'méxico': 'MEX', mejico: 'MEX',
  guatemala: 'GTM', 'costa rica': 'CRI', panama: 'PAN', 'panamá': 'PAN',

  // ── Oceania ──────────────────────────────────────────────────────────────────
  australia: 'AUS',
  'new zealand': 'NZL', aotearoa: 'NZL',

  // ── Africa ───────────────────────────────────────────────────────────────────
  'south africa': 'ZAF',
  morocco: 'MAR', egypt: 'EGY', nigeria: 'NGA', kenya: 'KEN',

  // ── City/region aliases seen in venue data (no comma to split on) ────────────
  bruxelles: 'BEL', brussels: 'BEL',
};

/** Map a single country display name to an ISO3 code (null if unknown / US). */
export function countryToIso3(name) {
  const key = String(name || '').toLowerCase().trim();
  if (!key) return null;
  // US is handled by affiliateRegionMap.js — never route it here.
  if (key === 'united states' || key === 'usa' || key === 'us'
    || key === 'united states of america' || key === 'u.s.' || key === 'u.s.a.'
    || key === 'america') {
    return null;
  }
  return COUNTRY_NAME_TO_ISO3[key] || null;
}

/**
 * Derive a venue's (non-US) country ISO3 from its location fields. Venues have
 * no `country` field — the country lives in `state`, either plain ("Germany")
 * or as "Region, Country" ("England, UK", "Ontario, Canada"). US state names
 * don't match the country map (and US venues are coloured by the dedicated US
 * tiers via coordinates), so they resolve to null here as intended.
 */
export function venueCountryIso3(venue) {
  if (!venue) return null;
  const candidates = [];
  if (venue.country) candidates.push(venue.country);
  const state = String(venue.state || '').trim();
  if (state.includes(',')) {
    // Try segments right→left: the country is usually the trailing segment,
    // but fall back to earlier ones for oddities like "Ireland, Republic".
    const parts = state.split(',').map((s) => s.trim()).filter(Boolean);
    for (let i = parts.length - 1; i >= 0; i--) candidates.push(parts[i]);
  } else if (state) {
    candidates.push(state);
  }
  for (const c of candidates) {
    const iso = countryToIso3(c);
    if (iso) return iso;
  }
  return null;
}

// Cache of decoded geometry, keyed `${iso3}:${level}` → { fc, entries } | null.
// A null value records a permanent miss (no boundaries at that level) so we
// don't keep re-requesting it.
const geoCache = new Map();
const inFlight = new Map();

// Decoded world ADM0 geometry: { fc, entries }. Loaded once for the session.
let worldGeo = null;
let worldPromise = null;

/** Load + decode the world country (ADM0) bundle once (cached for the session). */
export async function ensureWorldCountriesGeo() {
  if (worldGeo) return worldGeo;
  if (!worldPromise) {
    worldPromise = fetch(WORLD_COUNTRIES_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load world countries (${res.status})`);
        return res.json();
      })
      .then((topo) => {
        const decoded = topojsonFeature(topo, topo.objects.countries);
        const features = [];
        const entries = [];
        (decoded.features || []).forEach((f) => {
          const id = String(f.id);
          if (ADM0_EXCLUDE_IDS.has(id) || !f.geometry) return;
          f.properties = { ...(f.properties || {}), regionId: id };
          features.push(f);
          entries.push({ feature: f, id, bbox: turf.bbox(f) });
        });
        worldGeo = { fc: { type: 'FeatureCollection', features }, entries };
        return worldGeo;
      })
      .catch((err) => {
        worldPromise = null; // allow retry on next activation
        throw err;
      });
  }
  return worldPromise;
}

export function getWorldCountriesGeo() {
  return worldGeo;
}

function buildEntries(fc) {
  const features = [];
  const entries = [];
  (fc.features || []).forEach((f) => {
    if (!f.geometry) return;
    const props = f.properties || {};
    const id = String(props.shapeID || props.shapeName || `${features.length}`);
    f.properties = { ...props, regionId: id };
    features.push(f);
    entries.push({ feature: f, id, bbox: turf.bbox(f) });
  });
  return { fc: { type: 'FeatureCollection', features }, entries };
}

// geoBoundaries' download URLs point at `github.com/<owner>/<repo>/raw/<ref>/…`,
// which 302-redirects to the LFS media host. Browsers run a CORS check on that
// redirect response (which has no Access-Control-Allow-Origin), so the fetch
// fails. Rewriting to the final media URL avoids the redirect — media.github
// usercontent.com serves the real GeoJSON directly with `ACAO: *`.
function toCorsFriendlyUrl(url) {
  if (typeof url !== 'string') return url;
  const m = url.match(/^https:\/\/github\.com\/([^/]+)\/([^/]+)\/raw\/(.+)$/);
  if (m) return `https://media.githubusercontent.com/media/${m[1]}/${m[2]}/${m[3]}`;
  return url;
}

async function fetchLevel(iso3, level) {
  const metaRes = await fetch(`${GEOBOUNDARIES_API}/${iso3}/${level}/`);
  if (!metaRes.ok) {
    // 404 = that admin level doesn't exist for this country.
    if (metaRes.status === 404) return null;
    throw new Error(`geoBoundaries meta ${iso3}/${level} (${metaRes.status})`);
  }
  const meta = await metaRes.json();
  const rawUrl = meta.simplifiedGeometryGeoJSON || meta.gjDownloadURL;
  if (!rawUrl) return null;
  const geoRes = await fetch(toCorsFriendlyUrl(rawUrl));
  if (!geoRes.ok) throw new Error(`geoBoundaries geo ${iso3}/${level} (${geoRes.status})`);
  const fc = await geoRes.json();
  return buildEntries(fc);
}

/**
 * Load (and cache) the boundary geometry for one country at one admin level.
 * Falls back to the next-shallower level when the requested one is unavailable
 * (e.g. a small country with no ADM2), so every country still renders.
 * @returns {Promise<{ fc, entries, level } | null>}
 */
export async function ensureCountryRegions(iso3, level) {
  if (!iso3) return null;
  const levelIdx = Math.max(0, INTL_LEVELS.indexOf(level));
  for (let i = levelIdx; i >= 0; i--) {
    const lvl = INTL_LEVELS[i];
    const cacheKey = `${iso3}:${lvl}`;

    if (geoCache.has(cacheKey)) {
      const cached = geoCache.get(cacheKey);
      if (cached) return { ...cached, level: lvl };
      continue; // known miss → try shallower level
    }

    if (!inFlight.has(cacheKey)) {
      inFlight.set(cacheKey, fetchLevel(iso3, lvl)
        .then((res) => { geoCache.set(cacheKey, res); return res; })
        .catch((err) => {
          // Transient failure: don't poison the cache, allow a later retry.
          console.warn(`International region map: load failed ${cacheKey}`, err);
          return undefined;
        })
        .finally(() => { inFlight.delete(cacheKey); }));
    }

    // eslint-disable-next-line no-await-in-loop
    const res = await inFlight.get(cacheKey);
    if (res) return { ...res, level: lvl };
    if (res === null) continue; // confirmed miss → shallower level
    // res === undefined → transient error; stop here so we retry next time.
    return null;
  }
  return null;
}

/** Distinct non-US ISO3 codes among the supplied venues. */
export function collectVenueCountries(venues) {
  const set = new Set();
  for (const v of venues || []) {
    const iso = venueCountryIso3(v);
    if (iso) set.add(iso);
  }
  return [...set];
}
